<script lang="ts">
  /**
   * TaskDetailModal for a Now item (NowBar chips, the Now board). Call `open(item)`
   * through `bind:this`: it loads the item's project tasks, then shows the task.
   * Edits go through taskApi, so the project cache and Now stay in sync.
   */
  import { router } from '@inertiajs/svelte';
  import type { NowItem, Task } from '../lib/types';
  import { fetchProjectTasks, getCachedTasks } from '../lib/stores/projectTasksCache';
  import { completeTask, deleteTask, markTaskWontDo, updateTask } from '../lib/taskApi';
  import TaskDetailModal from './TaskDetailModal.svelte';

  let selectedTask = $state<Task | null>(null);
  let selectedProjectTitle = $state('');

  export async function open(item: Pick<NowItem, 'taskId' | 'projectId' | 'projectTitle'>) {
    const tasks = await fetchProjectTasks(item.projectId);
    const task = tasks.find((entry) => entry.id === item.taskId);
    if (!task) return;
    selectedTask = task;
    selectedProjectTitle = item.projectTitle;
  }

  function tasksFor(task: Task): Task[] {
    const cached = getCachedTasks(task.projectId).tasks;
    return cached.some((entry) => entry.id === task.id) ? cached : [task];
  }

  function keepSelectedIfOpen(task: Task, next: Task | undefined) {
    if (selectedTask?.id !== task.id) return;
    selectedTask = next ?? null;
  }

  async function handleUpdate(task: Task, updates: Partial<Task>) {
    const result = await updateTask(task.projectId, task.id, updates, tasksFor(task));
    keepSelectedIfOpen(
      task,
      result.tasks.find((entry) => entry.id === task.id),
    );
  }

  async function handleComplete(task: Task, pendingUpdates?: Partial<Task>) {
    if (pendingUpdates) await handleUpdate(task, pendingUpdates);
    const result = await completeTask(task.projectId, task.id, tasksFor(selectedTask ?? task));
    keepSelectedIfOpen(
      task,
      result.tasks.find((entry) => entry.id === task.id),
    );
  }

  async function handleWontDo(task: Task, pendingUpdates?: Partial<Task>) {
    const result = await markTaskWontDo(task.projectId, task.id, tasksFor(task), pendingUpdates);
    keepSelectedIfOpen(
      task,
      result.tasks.find((entry) => entry.id === task.id),
    );
  }

  function handleDelete(task: Task, pendingUpdates?: Partial<Task>) {
    if (pendingUpdates) void handleUpdate(task, pendingUpdates);
    void deleteTask(task.projectId, task.id, tasksFor(task));
    selectedTask = null;
  }
</script>

<TaskDetailModal
  task={selectedTask}
  projectTitle={selectedProjectTitle}
  projectUrl={selectedTask ? `/projects/${selectedTask.projectId}` : undefined}
  onClose={() => (selectedTask = null)}
  onUpdate={handleUpdate}
  onDelete={handleDelete}
  onMarkWontDo={handleWontDo}
  onToggleComplete={handleComplete}
  onProjectNavigate={(url) => {
    selectedTask = null;
    router.get(url);
  }}
/>
